import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, CreditCard, CheckCircle, XCircle, ExternalLink, Unlink } from 'lucide-react';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

export function StripeConnectSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingUrl, setOnboardingUrl] = useState(null);
  const { toast } = useToast();

  const fetchConnectStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/stripe-connect/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching Connect status:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Stripe Connect status',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConnectStatus();
  }, [fetchConnectStatus]);

  const startOnboarding = async () => {
    try {
      setLoading(true);
      const response = await api.post('/stripe-connect/onboard');
      
      if (response.data.onboarding_url) {
        setOnboardingUrl(response.data.onboarding_url);
        // Open onboarding in new window
        window.open(response.data.onboarding_url, '_blank');
      } else if (response.data.status === 'already_connected') {
        toast({
          title: 'Already Connected',
          description: 'Your Stripe account is already connected and ready to receive payments.',
        });
        fetchConnectStatus();
      }
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start Stripe Connect onboarding',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const disconnectAccount = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? You will not be able to receive card payments until you reconnect.')) {
      return;
    }

    try {
      setLoading(true);
      await api.post('/stripe-connect/disconnect');
      toast({
        title: 'Disconnected',
        description: 'Your Stripe account has been disconnected.',
      });
      fetchConnectStatus();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast({
        title: 'Error',
        description: 'Failed to disconnect Stripe account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-10 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-cyan-500" />
          <p className="mt-3 text-muted-foreground">Loading Stripe Connect status...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-4 w-4 text-cyan-500" /> Stripe Connect
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to receive card payments directly
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Display */}
        {status?.connected ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-green-800 dark:text-green-200">
                  Stripe Account Connected
                </p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Your Stripe account is active and ready to receive payments.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
                    Charges Enabled
                  </Badge>
                  {status.payouts_enabled && (
                    <Badge variant="outline" className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
                      Payouts Enabled
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Account ID: {status.account_id}
                </p>
              </div>
            </div>
          </div>
        ) : status?.status === 'pending' ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExternalLink className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Onboarding In Progress
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Please complete the Stripe onboarding process to start receiving payments.
                </p>
                {status.requirements?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Pending requirements:</p>
                    <ul className="text-sm text-amber-700 dark:text-amber-300 mt-1 list-disc list-inside">
                      {status.requirements.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-slate-400 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-700 dark:text-slate-300">
                  Not Connected
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Connect your Stripe account to accept card payments. Funds will be deposited directly to your bank account.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {!status?.connected ? (
            <Button 
              onClick={startOnboarding} 
              disabled={loading}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <CreditCard className="h-4 w-4 mr-2" />
              {status?.status === 'pending' ? 'Continue Onboarding' : 'Connect Stripe Account'}
            </Button>
          ) : (
            <>
              <Button 
                variant="outline"
                onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={disconnectAccount}
                disabled={loading}
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              >
                <Unlink className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </>
          )}
          <Button 
            variant="ghost" 
            onClick={fetchConnectStatus}
            disabled={loading}
          >
            Refresh Status
          </Button>
        </div>

        {/* Info Box */}
        <div className="text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Connect your existing Stripe account or create a new one</li>
            <li>Card payments are processed through your connected account</li>
            <li>Funds are deposited directly to your bank account (2-3 business days)</li>
            <li>A small platform fee (2.9%) is deducted from each transaction</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
